import * as React from 'react'
import { Button, Callout, Classes, Dialog, Intent } from '@blueprintjs/core'
import { useCallback, useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import { PriceInput } from '../../products_v5/components/JoinedForm/GoodwillfindsCard/Inputs'
import { S } from '../../../utils'
import Client from '../../../client'
import { showFailure, showToast } from '../../../Toast'
import APIErrorCallout from '../../../components/errors/APIErrorCallout'

import './RelistDialogStyle.scss'

interface Props {
  data: any
  actionTitle?: any
  isOpen: boolean
  currencyCode?: string
  recycledProductId?: any
  onClose: (didSomething?: boolean, failedIds?: string[]) => void
  onSubmit?: Function
  onSubmitted?: Function
  hasSold?: boolean
  marketAccountId: number
}

export const RelistDialog: React.FC<Props> = props => {
  const [price, setPrice] = useState(null)
  const listingIds = props.data

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<AxiosError>(undefined)

  const { onClose } = props
  const onCloseCancel = useCallback(() => {
    onClose(null, null)
  }, [onClose])

  // Initialize form values with admin settings, because this is what our users are accustomed to.
  // Users can clear this to use the current value.
  useEffect(() => {
    const fetchDefaults = async () => {
      setLoading(true)

      try {
        const res = await Client.getGoodwillFindsSettings(props.marketAccountId)

        setPrice(res.data.settings.defaultPrice)
      } catch (e) {
        showFailure('Could not fetch settings, please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchDefaults()
  }, [props.marketAccountId])

  const submit = async () => {
    setLoading(true)
    setError(undefined)

    if (props.onSubmit) {
      props.onSubmit({ price }, submittedError => {
        setLoading(false)
        setError(submittedError)
        if (!submittedError) props.onClose(true)
      })
      return
    }

    try {
      // Note, other channels will log this event. If we want this, we need to maintain the listing manager URL better.
      // if (readQueryParams().tab === 'attention-required') trackNeedsAttentionListingEvent('re-list', data.length)
      // https://app.asana.com/0/1206184798330168/1206815526196330

      // Note, dialogs for other channels strip out null values. That isn't necessary here yet.
      const res = await Client.bulkRelistGoodwillfinds(listingIds, { price })

      if (props.onSubmitted) props.onSubmitted()
      showToast(`Relisting ${listingIds.length} item${S(listingIds)}...`, Intent.PRIMARY)
      props.onClose(true, res.data.failed)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog title={props.actionTitle || 'Relist'} isOpen={props.isOpen} onClose={onCloseCancel}>
      <div className={Classes.DIALOG_BODY}>
        {error && <APIErrorCallout response={error} />}
        {props.recycledProductId && (
          <Callout intent={Intent.DANGER}>
            This listing&apos;s product is <strong>recycled</strong>. You must restore it first.
            <br />
            {/* Other channels support a "Restore" button plus onProductRestored. I'm skipping that for now. */}
          </Callout>
        )}

        {props.hasSold && (
          <Callout intent={Intent.WARNING}>
            You have selected a <strong>Sold</strong> listing. Please check to make sure this was intentional
          </Callout>
        )}

        <p className="extra-bottom-margin">Leave fields blank to use current values</p>

        <PriceInput value={price} onChange={e => setPrice(e.value)} placeholder="Use current" />
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button minimal disabled={loading} onClick={onCloseCancel}>
            Cancel
          </Button>
          <Button intent="primary" loading={loading} onClick={submit} disabled={props.recycledProductId}>
            {props.actionTitle || 'Relist'} {listingIds.length} item{S(listingIds)}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

export default RelistDialog
